import React, { useRef, useState } from 'react'
import PropTypes from 'prop-types'
import heic2any from 'heic2any'

import API from '../../services/API'

import S3Document from './S3Document'
import SmallModal from '../Modals/SmallModal'
import fileSizeLarge from '../../assets/lottie/fileSizeLarge.json'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import FileTooLarge from '../../partials/FileTooLarge'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner'
import { Divider, LinearProgress } from '@mui/material'

const S3DocumentUploadDisplay = ({
  title,
  caption,
  existingDocuments,
  s3folder,
  actionAfterSubmit,
  actionAfterDelete,
  maxSizeMB,
  viewOnly,
  showAsRows,
}) => {
  const [documents, setDocuments] = useState(existingDocuments ?? [])
  const [fileTooBigError, setFileTooBigError] = useState(false)
  const fileInput = useRef()
  const [uploadPending, setUploadPending] = useState(false)

  const handleDelete = (key) =>
    API.post('/deletes3object', { key }).then((res) => {
      if (res.status === 200) {
        const newDocsArray = documents.filter(
          (x) => x !== key && x?.s3key !== key,
        )
        actionAfterDelete(newDocsArray, key)
        setDocuments(newDocsArray)
      }
    })

  const handleFileUpload = async (e) => {
    setUploadPending(true)
    // let: HEIC photos are swapped for a converted JPEG below
    let file = e.target.files[0]
    const maxSize = maxSizeMB ?? 20 * 1024 * 1024
    if (file?.size > maxSize) {
      setFileTooBigError(true)
      setUploadPending(false)
      return
    }
    const fileType =
      file && typeof file.name === 'string'
        ? file.name.split('.').slice(-1)[0]
        : null
    const isHeic =
      fileType === 'image/heic' ||
      fileType === 'image/heif' ||
      /\.heic$/i.test(file.name) ||
      /\.heif$/i.test(file.name)

    if (isHeic) {
      try {
        const converted = await heic2any({
          blob: file,
          toType: 'image/jpeg',
          quality: 0.85,
        })
        // heic2any may return Blob or Blob[]
        const jpegBlob = Array.isArray(converted) ? converted[0] : converted

        file = new File(
          [jpegBlob],
          file.name.replace(/\.(heic|heif)$/i, '.jpg'),
          {
            type: 'image/jpeg',
          },
        )
      } catch (err) {
        console.error('HEIC conversion failed', err)
        return
      }
    }
    if (['jpeg', 'jpg', 'png', 'gif'].includes(fileType)) {
      file.localURL = URL.createObjectURL(e.target.files[0])
    } else if (fileType === 'pdf') {
      file.localICON = <PictureAsPdfIcon sx={{ fontSize: 40 }} />
    } else {
      file.localICON = <DocumentScannerIcon sx={{ fontSize: 40 }} />
    }
    let s3key
    let s3url
    await API.post('gets3uploadUrl', {
      folder: s3folder,
      extension: fileType,
    })
      .then((res) => {
        s3url = res.data.url
        s3key = res.data.key
        file.s3key = res.data.key
        API.put(s3url, file, {
          headers: {
            'Content-Type':
              fileType === 'pdf' ? 'application/pdf' : 'multipart/form-data',
          },
        }).then((res) => {
          if (res.status === 200) {
            actionAfterSubmit(s3key)
            setDocuments((prev) => [...prev, file.s3key])
          }
        })
      })
      .finally(() => setUploadPending(false))
  }

  return (
    <Stack gap={1}>
      {title && (
        <Typography variant="title2" fontSize="1rem">
          {title}
        </Typography>
      )}
      {caption && <Typography variant="caption">{caption}</Typography>}
      {!viewOnly && (
        <Box>
          <Button
            size="small"
            variant="contained"
            onClick={() => fileInput.current.click()}
          >
            Upload File
          </Button>
        </Box>
      )}
      {!viewOnly && (
        <input
          ref={fileInput}
          type="file"
          style={{ display: 'none' }}
          accept={[]}
          onChange={handleFileUpload}
        />
      )}
      {uploadPending && <LinearProgress />}
      {documents.length > 0 && (
        <Stack marginY={2} gap={2}>
          {!viewOnly && (
            <Typography variant="title3">
              Documents already uploaded:
            </Typography>
          )}
          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            flexWrap="wrap"
            columnGap={3}
            rowGap={1}
            alignItems={{ xs: 'center' }}
            divider={<Divider />}
          >
            {documents.map((doc) => (
              <S3Document
                key={doc}
                item={doc}
                onDelete={actionAfterDelete && handleDelete}
                showAsRow={showAsRows}
              />
            ))}
          </Stack>
        </Stack>
      )}
      <FileTooLarge
        open={fileTooBigError}
        onClose={() => setFileTooBigError(false)}
        limit={maxSizeMB ?? 20}
      />
    </Stack>
  )
}

S3DocumentUploadDisplay.propTypes = {
  title: PropTypes.string,
  caption: PropTypes.string,
  existingDocuments: PropTypes.array,
  s3folder: (props, propName, componentName) => {
    if (!props.viewOnly && !props[propName]) {
      return new Error(
        `Invalid prop \`${propName}\` supplied to \`${componentName}\`. Validation failed.`,
      )
    }
    return null
  },
  actionAfterSubmit: (props, propName, componentName) => {
    if (!props.viewOnly && typeof props[propName] !== 'function') {
      return new Error(
        `Invalid prop \`${propName}\` supplied to \`${componentName}\`. Validation failed.`,
      )
    }
    return null
  },
  actionAfterDelete: (props, propName, componentName) => {
    if (!props.viewOnly && typeof props[propName] !== 'function') {
      return new Error(
        `Invalid prop \`${propName}\` supplied to \`${componentName}\`. Validation failed.`,
      )
    }
    return null
  },
  maxSizeMB: PropTypes.number,
}

export default React.memo(S3DocumentUploadDisplay)
